import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CONVERSION_TRACK_COLOR, SEGMENT_COLORS, SEGMENT_ORDER, SegmentCounts } from '../utils/conversionSegments';
import { formatDecimal } from '../utils/metrics';

export interface ConversionSegmentMeterProps {
  label: string;
  counts: SegmentCounts;
  ratePct: number | null;
  muted?: boolean;
}

/**
 * One period's first packages as a bar split by outcome, the counts as a key, then the period's rate. A period in
 * which none expired keeps the label row and the empty track, and says so in place of the key and the rate.
 */
export const ConversionSegmentMeter: React.FC<ConversionSegmentMeterProps> = ({ label, counts, ratePct, muted }) => {
  const empty = counts.expired === 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ fontWeight: muted ? 400 : 600, color: muted ? 'text.secondary' : 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {`${counts.expired} expired`}
        </Typography>
      </Stack>
      <Box
        aria-hidden="true"
        sx={{ mt: 0.75, height: 12, display: 'flex', gap: '2px', borderRadius: '6px', overflow: 'hidden', bgcolor: empty ? CONVERSION_TRACK_COLOR : undefined }}
      >
        {empty
          ? null
          : SEGMENT_ORDER.filter((key) => counts[key] > 0).map((key) => (
              <Box key={key} sx={{ flex: `${counts[key] / counts.expired} 1 0`, bgcolor: SEGMENT_COLORS[key], opacity: muted ? 0.55 : 1 }} />
            ))}
      </Box>
      {empty ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          No first packages expired in this period
        </Typography>
      ) : (
        <>
          <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
            {`${counts.converted} converted · ${counts.finished} finished · ${counts.dropped} dropped`}
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: muted ? 16 : 22, fontWeight: muted ? 600 : 700, color: muted ? 'text.secondary' : 'text.primary' }}>
            {ratePct === null ? 'n/a' : `${formatDecimal(ratePct)}%`}
          </Typography>
        </>
      )}
    </Box>
  );
};
